import {
  CAMERA_PRESENTER,
  SCREEN_PRESENTER,
  UNASSIGNED,
} from '@shoflo/polymer';
import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  GET_BROADCAST,
  BROADCAST_GET_SOURCES,
  BROADCAST_CREATE_MEETING,
} from '@shoflo/studio-redux/actions/types';

import { deleteSources } from 'actions/source';

function _getAttendeeSources(sources) {
  return sources.filter((src) =>
    [CAMERA_PRESENTER, SCREEN_PRESENTER].includes(src.type)
  );
}

/**
 * Get a list od source IDs given a list of sources
 *
 * @param {*} sources
 * @returns
 */
function _getSourceIds(sources) {
  return sources.map((s) => s._id);
}

/**
 * Get a list of source IDs to delete
 *
 * @param {*} meetingStatus
 * @param {*} sources
 * @param {*} attendeeResponse
 */
function _getSourceIdsToDelete(meetingStatus, sources, attendeeResponse) {
  const unassignedSources = _getAttendeeSources(sources).filter(
    (src) => src.value === UNASSIGNED
  );

  let sourcesToDelete = _getSourceIds(unassignedSources);

  if (meetingStatus === 'active') {
    const staleScreenPresenterSources = _getAttendeeSources(sources).filter(
      (src) => src.value === `${attendeeResponse?.Attendee?.AttendeeId}#content`
    );
    let staleScreenPresenterSourcesIds = _getSourceIds(
      staleScreenPresenterSources
    );
    sourcesToDelete = [...sourcesToDelete, ...staleScreenPresenterSourcesIds];
  }

  /**
   * If the meeting is inactive, we want to clear out all assigned sources as well
   */
  if (meetingStatus === 'inactive') {
    const assignedSources = _getAttendeeSources(sources).filter(
      (src) => src.value !== UNASSIGNED
    );
    const assignedSourcesIds = _getSourceIds(assignedSources);
    sourcesToDelete = [...sourcesToDelete, ...assignedSourcesIds];
  }

  return sourcesToDelete;
}

/**
 * What does this middleware do?
 *
 * It checks for camera and screen sources in a broadcast,
 * deleting the unassigned sources on first load of the page,
 * targeted by the GET_SOURCES_SUCCESS, GET_BROADCAST_SUCCESS and BROADCAST_CREATE_MEETING_SUCCESS actions.
 * It will also delete assigned Camera and Screen Sources if the current meeting session has ended i.e (inactive)
 * It will also remove unassigned sources from program.
 *
 * @param {*} store
 */

function resetSources(store) {
  //Use this to keep track of how many items have been processed in the stream, so we can stop processing after the first one
  let _taken = 0;

  let gotSources = false;

  let gotBroadcast = false;

  let isMeetingCreated = false;

  let meetingStatus = '';

  let broadcastMeetingId = null;

  let createdMeetingId = null;

  return (next) => (action) => {
    const targetedAction = [
      `${GET_BROADCAST}_SUCCESS`,
      `${BROADCAST_GET_SOURCES}_SUCCESS`,
      `${BROADCAST_CREATE_MEETING}_SUCCESS`,
    ];
    from([action])
      .pipe(
        filter(($action) => {
          return targetedAction.includes($action.type);
        })
      )
      .subscribe(async ($action) => {
        // only run once on page load.
        if (_taken && gotSources && gotBroadcast && isMeetingCreated) return;

        //We are doing this because for some unknown reason the .take() operator is not working. 2 hours were spent trying to get it to work, there appears to be
        //a problem w/ our current setup that does not allow for .take() to actually unsubscribe properly
        _taken++;

        const { dispatch, getState } = store;

        let {
          broadcastSources: { sources },
          meeting: { attendeeResponse },
        } = getState();

        const { type, payload } = $action;

        if (type === `${GET_BROADCAST}_SUCCESS`) {
          gotBroadcast = true;
          broadcastMeetingId = payload.meetingId;
        }

        if (type === `${BROADCAST_GET_SOURCES}_SUCCESS`) {
          gotSources = true;
          sources = payload.items;
        }

        if (type === `${BROADCAST_CREATE_MEETING}_SUCCESS`) {
          isMeetingCreated = true;
          createdMeetingId = payload.meetingResponse.Meeting.MeetingId;
        }

        if (gotSources && gotBroadcast && isMeetingCreated) {
          // If the meeting ID in create meeting response is same as the meeting ID in get broadcast action payload,
          // it means the meeting is still active, else it is inactive.
          meetingStatus =
            createdMeetingId === broadcastMeetingId ? 'active' : 'inactive';
          const sourceIdsToDelete = _getSourceIdsToDelete(
            meetingStatus,
            sources,
            attendeeResponse
          );
          if (sourceIdsToDelete.length > 0) {
            dispatch(deleteSources(sourceIdsToDelete));
          }
        }
      });

    return next(action);
  };
}

export default resetSources;
